import type { HTMLAttributes, Ref } from 'react'
import { cx } from '@/shared/lib'
import s from './Surface.module.css'

/** Материал поверхности: фон, цвет рамки и сила размытия. */
export type SurfaceVariant = 'panel' | 'card' | 'inset' | 'bar' | 'overlay'
export type SurfaceRadius = 'none' | 'menu' | 'card' | 'block' | 'panel'
export type SurfaceElevation = 'none' | 'card' | 'panel' | 'menu' | 'modal'
export type SurfacePadding = 'none' | 'xs' | 'sm' | 'md' | 'lg'
/** Только контейнерные теги: материал не превращается в кнопку или ссылку. */
export type SurfaceElement = 'div' | 'section' | 'article' | 'aside' | 'header' | 'footer' | 'nav'

export type SurfaceProps = {
  as?: SurfaceElement
  variant?: SurfaceVariant
  radius?: SurfaceRadius
  elevation?: SurfaceElevation
  padding?: SurfacePadding
  /** Выключается для длинных виртуализованных списков: 40 размытий на экран кладут WebView. */
  blur?: boolean
  /** Только визуальная афордансная подсветка. Роль, tabIndex и обработчик даёт потребитель. */
  interactive?: boolean
  ref?: Ref<HTMLElement>
} & HTMLAttributes<HTMLElement>

type SurfacePreset = {
  radius: SurfaceRadius
  elevation: SurfaceElevation
  padding: SurfacePadding
  blur: boolean
}

// CSS-модуль типизирован индексной сигнатурой, поэтому значения — string | undefined
const VARIANT_CLASS: Record<SurfaceVariant, string | undefined> = {
  panel: s.variantPanel,
  card: s.variantCard,
  inset: s.variantInset,
  bar: s.variantBar,
  overlay: s.variantOverlay,
}

const RADIUS_CLASS: Record<SurfaceRadius, string | undefined> = {
  none: s.radiusNone,
  menu: s.radiusMenu,
  card: s.radiusCard,
  block: s.radiusBlock,
  panel: s.radiusPanel,
}

const ELEVATION_CLASS: Record<SurfaceElevation, string | undefined> = {
  none: undefined,
  card: s.elevationCard,
  panel: s.elevationPanel,
  menu: s.elevationMenu,
  modal: s.elevationModal,
}

const PADDING_CLASS: Record<SurfacePadding, string | undefined> = {
  none: undefined,
  xs: s.paddingXs,
  sm: s.paddingSm,
  md: s.paddingMd,
  lg: s.paddingLg,
}

/** Пресеты пяти уровней кита: см. таблицу в docs/ui-kit/contracts/surface.md. */
const PRESETS: Record<SurfaceVariant, SurfacePreset> = {
  panel: { radius: 'panel', elevation: 'panel', padding: 'lg', blur: true },
  card: { radius: 'card', elevation: 'card', padding: 'md', blur: true },
  inset: { radius: 'block', elevation: 'none', padding: 'md', blur: false },
  bar: { radius: 'block', elevation: 'panel', padding: 'none', blur: true },
  overlay: { radius: 'menu', elevation: 'menu', padding: 'xs', blur: true },
}

export function Surface({
  as = 'div',
  variant = 'card',
  radius,
  elevation,
  padding,
  blur,
  interactive = false,
  className,
  children,
  ref,
  ...rest
}: SurfaceProps) {
  const preset = PRESETS[variant]
  const Tag = as
  const appliedRadius = radius ?? preset.radius
  const appliedElevation = elevation ?? preset.elevation
  const appliedPadding = padding ?? preset.padding
  const appliedBlur = blur ?? preset.blur

  return (
    <Tag
      {...rest}
      // Тег выбирается из закрытого union, поэтому JSX не выводит конкретный HTML-элемент;
      // единственное приведение в компоненте — на этой границе типов.
      ref={ref as Ref<HTMLDivElement>}
      className={cx(
        s.root,
        VARIANT_CLASS[variant],
        RADIUS_CLASS[appliedRadius],
        ELEVATION_CLASS[appliedElevation],
        PADDING_CLASS[appliedPadding],
        !appliedBlur && s.noBlur,
        interactive && s.interactive,
        className,
      )}
      data-variant={variant}
      data-radius={appliedRadius}
      data-elevation={appliedElevation}
      data-padding={appliedPadding}
      data-blur={String(appliedBlur)}
      data-interactive={interactive ? 'true' : undefined}
    >
      {children}
    </Tag>
  )
}
